import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Psychology,
  AutoAwesome,
  CheckCircle,
  Warning,
  Error,
  FormatQuote,
  Summarize,
  Rule,
  School,
  VerifiedUser,
} from '@mui/icons-material';
import { PageHeader, Loading, ScoreChip } from './common';
import { apiService } from '../services/api';

const RubricLevelChip = ({ level }) => {
  const lower = (level || '').toLowerCase();
  let color = 'default';
  let icon = <Psychology fontSize="small" />;
  if (lower.includes('highly effective') || lower.includes('he')) {
    color = 'success';
    icon = <CheckCircle fontSize="small" />;
  } else if (lower.includes('good') || lower.includes('near')) {
    color = 'info';
    icon = <AutoAwesome fontSize="small" />;
  } else if (lower.includes('effective') && !lower.includes('ineffective')) {
    color = 'primary';
    icon = <CheckCircle fontSize="small" />;
  } else if (lower.includes('borderline')) {
    color = 'warning';
    icon = <Warning fontSize="small" />;
  } else if (lower.includes('ineffective')) {
    color = 'error';
    icon = <Error fontSize="small" />;
  }
  return <Chip icon={icon} label={level || 'Unknown'} color={color} size="small" />;
};

const EvidenceCitationTable = ({ citations }) => {
  if (!citations || citations.length === 0) {
    return <Typography variant="body2" color="text.secondary">No evidence citations.</Typography>;
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'action.hover' }}>
            <TableCell>Competency Element</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Quote</TableCell>
            <TableCell>Relevance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {citations.map((c, idx) => (
            <TableRow key={idx} hover>
              <TableCell><Typography variant="body2" fontWeight={600}>{c.competency_element}</Typography></TableCell>
              <TableCell><Chip label={c.source_section} size="small" variant="outlined" /></TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <FormatQuote fontSize="small" color="action" sx={{ mt: 0.5, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.quote}</Typography>
                </Box>
              </TableCell>
              <TableCell><Typography variant="body2" color="text.secondary">{c.relevance_to_score}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Normalize the guideline to a string — LLM may return an object instead of a string
const normalizeGuideline = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return Object.entries(val)
      .map(([k, v]) => `${k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:\n${typeof v === 'string' ? v : JSON.stringify(v, null, 2)}`)
      .join('\n\n');
  }
  return String(val);
};

const buildExplanationText = (explanation) => {
  // Prefer the generalized guideline when creating Guidelines/Few-Shot Examples
  const generalized = normalizeGuideline(explanation.generalized_scoring_guideline);
  if (generalized) {
    return generalized;
  }
  const parts = [explanation.explanation];
  if (explanation.score_justification_summary) {
    parts.push(`\n\nJustification: ${explanation.score_justification_summary}`);
  }
  if (explanation.supporting_indicators?.length) {
    parts.push(`\n\nSupporting indicators: ${explanation.supporting_indicators.join(', ')}`);
  }
  if (explanation.derailer_indicators_observed?.length) {
    parts.push(`\n\nDerailer indicators: ${explanation.derailer_indicators_observed.join(', ')}`);
  }
  return parts.filter(Boolean).join('');
};

const ValidationResultsDisplay = ({ validationStatus, onCreateExample }) => {
  const { is_valid, iterations_log, random_test_results, validated_few_shot_text, ground_truth_score, aspect_max_score } = validationStatus;
  const maxScore = aspect_max_score || 10;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Overall result */}
      <Alert severity={is_valid ? 'success' : 'warning'} icon={is_valid ? <CheckCircle /> : <Warning />}>
        {is_valid ? 'Few-shot example VALIDATED — scores match ground truth for all tested candidates' : 'Few-shot example NOT VALIDATED — scores do not match ground truth within tolerance'}
      </Alert>

      {/* Iteration history */}
      {iterations_log?.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={700}>Revision Iterations</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell>Iteration</TableCell>
                  <TableCell>Eval Score</TableCell>
                  <TableCell>GT Score</TableCell>
                  <TableCell>Delta</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell>Revised</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {iterations_log.map((iter, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{iter.iteration}</TableCell>
                    <TableCell><ScoreChip score={iter.eval_score} maxScore={maxScore} /></TableCell>
                    <TableCell><ScoreChip score={iter.gt_score} maxScore={maxScore} /></TableCell>
                    <TableCell>{iter.delta > 0 ? '+' : ''}{Number(iter.delta).toFixed(2)}</TableCell>
                    <TableCell><Chip label={iter.match ? 'Pass' : 'Fail'} color={iter.match ? 'success' : 'error'} size="small" /></TableCell>
                    <TableCell>{iter.revised ? <AutoAwesome fontSize="small" color="warning" /> : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Cross-validation results */}
      {random_test_results?.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={700}>Cross-Validation (Random Candidates)</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell>Candidate</TableCell>
                  <TableCell>Eval Score</TableCell>
                  <TableCell>GT Score</TableCell>
                  <TableCell>Delta</TableCell>
                  <TableCell>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {random_test_results.map((test, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{test.candidate_id || '—'}</TableCell>
                    {test.error ? (
                      <TableCell colSpan={4}><Typography variant="body2" color="error">{test.error}</Typography></TableCell>
                    ) : (
                      <>
                        <TableCell><ScoreChip score={test.eval_score} maxScore={maxScore} /></TableCell>
                        <TableCell><ScoreChip score={test.gt_score} maxScore={maxScore} /></TableCell>
                        <TableCell>{test.delta > 0 ? '+' : ''}{Number(test.delta).toFixed(2)}</TableCell>
                        <TableCell><Chip label={test.match ? 'Pass' : 'Fail'} color={test.match ? 'success' : 'error'} size="small" /></TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Validated text */}
      {validated_few_shot_text && (
        <>
          <Typography variant="subtitle2" fontWeight={700}>Validated Few-Shot Text</Typography>
          <TextField
            value={validated_few_shot_text}
            multiline
            minRows={4}
            maxRows={16}
            fullWidth
            InputProps={{ readOnly: true }}
            variant="outlined"
          />
        </>
      )}

      {/* Action button */}
      {validated_few_shot_text && (
        <Button
          variant="contained"
          color="secondary"
          startIcon={<School />}
          onClick={() => onCreateExample(validated_few_shot_text)}
        >
          Create as Few-Shot Example
        </Button>
      )}
    </Box>
  );
};

const AspectExplanationCard = ({ explanation, onCreateGuideline, onCreateExample, onValidate }) => {
  const isCP = (explanation.leadership_capability_group_name || '').toUpperCase() === 'COUNTERPRODUCTIVE';
  const hasGuideline = !!normalizeGuideline(explanation.generalized_scoring_guideline);

  return (
    <Card variant="outlined" sx={{ borderLeft: 4, borderColor: isCP ? 'error.main' : 'primary.main' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {explanation.leadership_capability_name}
            </Typography>
            {isCP && <Chip label="Counterproductive" color="error" size="small" />}
            <RubricLevelChip level={explanation.rubric_level_identified} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">GT Score:</Typography>
            <ScoreChip score={explanation.ground_truth_score} maxScore={explanation.max_score} />
            <Button size="small" variant="outlined" startIcon={<Rule fontSize="small" />} onClick={() => onCreateGuideline(explanation)}>
              Guideline
            </Button>
            <Button size="small" variant="outlined" color="secondary" startIcon={<School fontSize="small" />} onClick={() => onCreateExample(explanation)}>
              Example
            </Button>
            <Button size="small" variant="outlined" color="warning" startIcon={<VerifiedUser fontSize="small" />} onClick={() => onValidate(explanation)} disabled={!hasGuideline}>
              Validate
            </Button>
          </Box>
        </Box>

        {/* Score Justification Summary */}
        {explanation.score_justification_summary && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<Summarize fontSize="small" />}>
            {explanation.score_justification_summary}
          </Alert>
        )}

        {/* Full Explanation */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Explanation
        </Typography>
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {explanation.explanation}
          </Typography>
        </Box>

        {/* Generalized Scoring Guideline */}
        {normalizeGuideline(explanation.generalized_scoring_guideline) && (
          <>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              <School fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Generalized Scoring Guideline
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }} icon={<AutoAwesome fontSize="small" />}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {normalizeGuideline(explanation.generalized_scoring_guideline)}
              </Typography>
            </Alert>
          </>
        )}

        {/* Evidence Citations */}
        <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2" fontWeight={700}>
              Evidence Citations ({explanation.evidence_citations?.length || 0})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <EvidenceCitationTable citations={explanation.evidence_citations} />
          </AccordionDetails>
        </Accordion>

        {/* Indicators */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {explanation.supporting_indicators && explanation.supporting_indicators.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                <CheckCircle fontSize="small" color="success" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Supporting Indicators
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {explanation.supporting_indicators.map((ind, idx) => (
                  <Chip key={idx} label={ind} size="small" color="success" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
          {explanation.derailer_indicators_observed && explanation.derailer_indicators_observed.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                <Error fontSize="small" color="error" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Derailer Indicators
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {explanation.derailer_indicators_observed.map((ind, idx) => (
                  <Chip key={idx} label={ind} size="small" color="error" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const GroundTruthExplainer = () => {
  const [candidateId, setCandidateId] = useState('');
  const [controlSumId, setControlSumId] = useState('');
  const [aspectName, setAspectName] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guideline/Example creation dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState('guideline'); // 'guideline' or 'fewShot'
  const [createForm, setCreateForm] = useState({ name: '', aspect: '', guideline_text: '', few_shot_example_text: '', is_active: true });
  const [aspects, setAspects] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Validation state
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationRunId, setValidationRunId] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationPolling, setValidationPolling] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const pollingRef = useRef(null);

  const loadAspects = useCallback(async () => {
    try {
      const res = await apiService.aspects.list(7);
      setAspects(res.data || []);
    } catch (err) {
      console.error('Failed to load aspects:', err);
    }
  }, []);

  useEffect(() => {
    loadAspects();
  }, [loadAspects]);

  // Polling for validation status
  useEffect(() => {
    if (!validationPolling || !validationRunId) return;

    pollingRef.current = setInterval(async () => {
      try {
        const res = await apiService.fewShotValidation.getStatus(validationRunId);
        setValidationStatus(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setValidationPolling(false);
          clearInterval(pollingRef.current);
        }
      } catch (err) {
        setValidationPolling(false);
        clearInterval(pollingRef.current);
        setValidationError('Failed to check validation status: ' + (err.response?.data?.error || err.message));
      }
    }, 3000);

    return () => clearInterval(pollingRef.current);
  }, [validationPolling, validationRunId]);

  const handleExplain = async () => {
    if (!candidateId.trim()) {
      setError('Candidate ID is required.');
      return;
    }

    const data = { candidate_id: parseInt(candidateId.trim()) };
    if (controlSumId.trim()) data.control_sum_id = parseInt(controlSumId.trim());
    if (aspectName.trim()) data.aspect_name = aspectName.trim();

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const res = await apiService.groundTruth.explain(data);
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to get ground truth explanations.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleExplain();
  };

  const openCreateDialog = (explanation, type) => {
    const matchedAspect = aspects.find(a => a.control_sum_id === explanation.control_sum_id);
    const text = buildExplanationText(explanation);
    setCreateType(type);
    setCreateForm({
      name: `${type === 'guideline' ? 'Guideline' : 'Example'}: ${explanation.leadership_capability_name}`,
      aspect: matchedAspect ? matchedAspect.id : '',
      guideline_text: type === 'guideline' ? text : '',
      few_shot_example_text: type === 'fewShot' ? text : '',
      is_active: true,
    });
    setCreateDialogOpen(true);
  };

  const openValidationDialog = async (explanation) => {
    const fewShotText = normalizeGuideline(explanation.generalized_scoring_guideline);
    if (!fewShotText) {
      setSnackbar({ open: true, message: 'No generalized scoring guideline available to validate.', severity: 'error' });
      return;
    }

    setValidationError(null);
    setValidationStatus(null);

    try {
      const res = await apiService.fewShotValidation.start({
        candidate_id: explanation.candidate_id,
        control_sum_id: explanation.control_sum_id,
        few_shot_text: fewShotText,
      });
      setValidationRunId(res.data.validation_id);
      setValidationDialogOpen(true);
      setValidationPolling(true);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to start validation.', severity: 'error' });
    }
  };

  const handleCreateFromValidated = (text) => {
    const matchedAspect = aspects.find(a => a.control_sum_id === validationStatus?.control_sum_id);
    setCreateType('fewShot');
    setCreateForm({
      name: `Validated Example: ${validationStatus?.aspect_name || ''}`,
      aspect: matchedAspect ? matchedAspect.id : '',
      guideline_text: '',
      few_shot_example_text: text,
      is_active: true,
    });
    setValidationDialogOpen(false);
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!createForm.aspect) {
      setSnackbar({ open: true, message: 'Please select an aspect.', severity: 'error' });
      return;
    }
    setCreateLoading(true);
    try {
      if (createType === 'guideline') {
        await apiService.guidelines.create({
          name: createForm.name,
          aspect: Number(createForm.aspect),
          guideline_text: createForm.guideline_text,
          is_active: createForm.is_active,
        });
        setSnackbar({ open: true, message: 'Guideline created successfully!', severity: 'success' });
      } else {
        await apiService.fewShotExamples.create({
          name: createForm.name,
          aspect: Number(createForm.aspect),
          few_shot_example_text: createForm.few_shot_example_text,
          is_active: createForm.is_active,
        });
        setSnackbar({ open: true, message: 'Few-shot example created successfully!', severity: 'success' });
      }
      setCreateDialogOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || err.response?.data?.detail || 'Failed to create.', severity: 'error' });
    } finally {
      setCreateLoading(false);
    }
  };

  const avgScore = results?.explanations?.length > 0
    ? results.explanations.reduce((sum, e) => sum + (e.ground_truth_score || 0), 0) / results.explanations.length
    : 0;

  const cpCount = results?.explanations?.filter(
    (e) => (e.leadership_capability_group_name || '').toUpperCase() === 'COUNTERPRODUCTIVE'
  ).length || 0;

  return (
    <Box>
      <PageHeader
        title="Ground Truth Explainer"
        subtitle="Analyze why ground truth scores were assigned using LLM-powered explanation"
      />

      {/* Search Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Explain Ground Truth Scores
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a candidate ID to have the LLM analyze the respondent data and explain why each ground truth score was assigned.
            Optionally filter to a specific aspect.
          </Typography>

          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Candidate ID"
                value={candidateId}
                onChange={(e) => setCandidateId(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                size="small"
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Control Sum ID"
                value={controlSumId}
                onChange={(e) => setControlSumId(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                size="small"
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Aspect Name"
                value={aspectName}
                onChange={(e) => setAspectName(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                size="small"
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleExplain}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Search />}
                fullWidth
                size="small"
                sx={{ height: 40 }}
              >
                {loading ? 'Analyzing...' : 'Explain'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {loading && (
        <Loading message="LLM is analyzing ground truth scores... This may take several minutes for all aspects." size="large" />
      )}

      {results && !loading && (
        <>
          {/* Summary Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Aspects Explained</Typography>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {results.aspects_explained}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Avg GT Score</Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {avgScore.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Counterproductive</Typography>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {cpCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Errors</Typography>
                  <Typography variant="h4" fontWeight={700} color={results.aspects_with_errors > 0 ? 'error.main' : 'success.main'}>
                    {results.aspects_with_errors}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Errors */}
          {results.errors?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {results.errors.map((e, idx) => (
                <Typography key={idx} variant="body2">
                  Control Sum {e.control_sum_id} ({e.leadership_capability_name}): {e.error}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Aspect Explanations */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {results.explanations.map((explanation, idx) => (
              <AspectExplanationCard
                key={idx}
                explanation={explanation}
                onCreateGuideline={(exp) => openCreateDialog(exp, 'guideline')}
                onCreateExample={(exp) => openCreateDialog(exp, 'fewShot')}
                onValidate={(exp) => openValidationDialog(exp)}
              />
            ))}
          </Box>

          {results.explanations.length === 0 && (
            <Card>
              <CardContent>
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No explanations were generated.
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Create Guideline / Few-Shot Example Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createType === 'guideline' ? 'Create Guideline' : 'Create Few-Shot Example'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Aspect</InputLabel>
              <Select
                value={createForm.aspect}
                onChange={(e) => setCreateForm(prev => ({ ...prev, aspect: e.target.value }))}
                label="Aspect"
              >
                {aspects.map(a => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name} (ID: {a.control_sum_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Name"
              value={createForm.name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={<Switch checked={createForm.is_active} onChange={(e) => setCreateForm(prev => ({ ...prev, is_active: e.target.checked }))} />}
              label="Active"
            />
            {createType === 'guideline' ? (
              <TextField
                label="Guideline Text"
                value={createForm.guideline_text}
                onChange={(e) => setCreateForm(prev => ({ ...prev, guideline_text: e.target.value }))}
                fullWidth
                multiline
                minRows={6}
                maxRows={20}
              />
            ) : (
              <TextField
                label="Few-Shot Example Text"
                value={createForm.few_shot_example_text}
                onChange={(e) => setCreateForm(prev => ({ ...prev, few_shot_example_text: e.target.value }))}
                fullWidth
                multiline
                minRows={6}
                maxRows={20}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={createLoading || !createForm.aspect}>
            {createLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog open={validationDialogOpen} onClose={() => { if (!validationPolling) setValidationDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedUser />
          Few-Shot Validation
        </DialogTitle>
        <DialogContent>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>
          )}
          {(!validationStatus || validationStatus.status === 'queued' || validationStatus.status === 'running') && !validationError && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6">Validating few-shot example...</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Iteration {(validationStatus?.iterations_log?.length || 0) + 1} of 3
              </Typography>
              {validationStatus?.iterations_log?.length > 0 && (
                <Box sx={{ width: '100%', mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Progress</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Iter</TableCell>
                          <TableCell>Eval</TableCell>
                          <TableCell>GT</TableCell>
                          <TableCell>Delta</TableCell>
                          <TableCell>Match</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationStatus.iterations_log.map((iter, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{iter.iteration}</TableCell>
                            <TableCell>{Number(iter.eval_score).toFixed(2)}</TableCell>
                            <TableCell>{Number(iter.gt_score).toFixed(2)}</TableCell>
                            <TableCell>{iter.delta > 0 ? '+' : ''}{Number(iter.delta).toFixed(2)}</TableCell>
                            <TableCell><Chip label={iter.match ? 'Pass' : 'Fail'} color={iter.match ? 'success' : 'error'} size="small" /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
          {validationStatus?.status === 'completed' && (
            <ValidationResultsDisplay validationStatus={validationStatus} onCreateExample={handleCreateFromValidated} />
          )}
          {validationStatus?.status === 'failed' && !validationError && (
            <Alert severity="error">Validation failed. {validationStatus.error_message || 'The validation workflow encountered an error. Please try again.'}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialogOpen(false)} disabled={validationPolling}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroundTruthExplainer;